/**
 * Dashboard viewer selection — backs the Mine/Peer toggle in
 * `DashboardHeader` (Slice F, ADR-0082).
 *
 * Reads `?viewer=<email>` from the route's validated search (see
 * `parseDashboardSearch` in router.tsx). If absent or equal to the
 * signed-in operator's email (case-insensitive), the viewer IS the
 * signed-in operator — we keep the URL clean by omitting the param in
 * that case.
 *
 * Returned `viewerForApi` is the email to pass into `useDashboard` /
 * `useDashboardCount` — undefined when viewing Mine, so the existing
 * "no arg = signed-in" default in those hooks holds and the wire stays
 * parameter-free. The page renders identically to pre-Slice-F when no
 * one has clicked the toggle.
 *
 * Coupling: this hook is the *only* place that touches the router for
 * the Dashboard. The page itself reads `{viewer, viewerForApi, isMine,
 * setViewer}` and never sees `useRouterState` / `useNavigate` directly,
 * which keeps `DashboardPage` testable without a Router provider.
 */

import { useNavigate, useRouterState } from "@tanstack/react-router";

export interface ViewerSelection {
  /** The resolved viewer email — always set (signed-in fallback). */
  viewer: string;
  /** Value to pass to `useDashboard` / `useDashboardCount` — undefined when Mine. */
  viewerForApi: string | undefined;
  /** True when the resolved viewer === the signed-in operator. */
  isMine: boolean;
  /** Switch the toggle. Clears the URL param when targeting Mine. */
  setViewer: (email: string) => void;
}

export function useViewerSelection(signedInEmail: string): ViewerSelection {
  const navigate = useNavigate();
  const rawViewer = useRouterState({
    select: (s) => {
      const search = s.location.search as Record<string, unknown>;
      const v = search?.viewer;
      return typeof v === "string" ? v.trim() : "";
    },
  });

  const lowerSelf = signedInEmail.trim().toLowerCase();
  const lowerRaw = rawViewer.toLowerCase();
  const isMine = lowerRaw === "" || lowerRaw === lowerSelf;
  const viewer = isMine ? signedInEmail : rawViewer;
  const viewerForApi = isMine ? undefined : rawViewer;

  function setViewer(email: string) {
    const targetIsMine = email.trim().toLowerCase() === lowerSelf;
    navigate({
      to: ".",
      search: (prev: Record<string, unknown>) => {
        const next = { ...prev };
        if (targetIsMine) {
          delete next.viewer;
        } else {
          next.viewer = email.trim();
        }
        return next;
      },
      replace: true,
    });
  }

  return { viewer, viewerForApi, isMine, setViewer };
}
